import axios from "axios";
import type { AxiosRequestConfig } from "axios";
import { ElMessage } from "element-plus";
import router from "@/router/index";
import { getToken, TOKEN_KEY, clearToken } from "./common";
import i18n, { getCurrentLocale } from "@/i18n";

// Friendlier than the raw backend strings ("Token Invalid", "Permission Denied").
// Falls through to the literal key if the locale file ever loses the entry.
function authMsg(key: "sessionExpired" | "permissionDenied" | "networkError"): string {
	return i18n.global.t(`auth.${key}`);
}

if (import.meta.env.VITE_USER_NODE_ENV === "development") {
	axios.defaults.baseURL = "/dev-api";
} else {
	// Use same-origin requests so a single build works on any host
	// (web.imles.cn, web.prep.imles.cn, web.mycoach.health)
	axios.defaults.baseURL = "";
}

const pendingMap = new Map();

function getPendingKey(config: AxiosRequestConfig = {}) {
	let { data } = config;
	const { url, method, params, headers } = config;
	if (typeof data === "string") data = JSON.parse(data);
	return [
		url,
		method,
		headers && headers[TOKEN_KEY] ? headers[TOKEN_KEY] : "",
		JSON.stringify(params),
		JSON.stringify(data),
	].join("&");
}

function addPending(config: AxiosRequestConfig = {}) {
	const pendingKey = getPendingKey(config);
	config.cancelToken =
		config.cancelToken ||
		new axios.CancelToken((cancel) => {
			if (!pendingMap.has(pendingKey)) {
				pendingMap.set(pendingKey, cancel);
			}
		});
}

function removePending(config: AxiosRequestConfig = {}) {
	const pendingKey = getPendingKey(config);
	if (pendingMap.has(pendingKey)) {
		const cancelToken = pendingMap.get(pendingKey);
		cancelToken(pendingKey);
		pendingMap.delete(pendingKey);
	}
}

axios.interceptors.request.use(
	function (config) {
		removePending(config);
		addPending(config);

		config.headers["app-code"] = "WEB";
		// COCHE §10.1.10 — surface the persisted locale to the backend Jackson swap.
		config.headers["X-Imles-Locale"] = getCurrentLocale();
		const token = getToken();
		if (token) {
			config.headers["access-token"] = token;
		}

		return config;
	},
	function (error) {
		return Promise.reject(error);
	},
);

axios.interceptors.response.use(
	function (response) {
		removePending(response.config);
		if (response.status === 200) {
			let data = null;

			if (response.config.responseType === "blob") {
				data = response;
			} else {
				data = response.data;
			}
			if (response.data.status && response.data.status !== 200) {
				const isAuthExpired = response.data.status === 401;
				const isForbidden = response.data.status === 403;
				ElMessage.closeAll();
				ElMessage({
					type: "error",
					message: isAuthExpired
						? authMsg("sessionExpired")
						: isForbidden
							? authMsg("permissionDenied")
							: response.data.message,
				});
				if (isAuthExpired) {
					clearToken();
					router.push({ path: "/login" });
				}
				return Promise.reject(data);
			}
			return data;
		} else {
			const isAuthExpired = response.status === 401;
			const isForbidden = response.status === 403;
			ElMessage.closeAll();
			ElMessage({
				type: "error",
				message: isAuthExpired
					? authMsg("sessionExpired")
					: isForbidden
						? authMsg("permissionDenied")
						: response.data?.message,
			});
			if (isAuthExpired) {
				clearToken();
				router.push({ path: "/login" });
			}
			return Promise.reject(response);
		}
	},
	function (error) {
		removePending(error.config);

		// Handle canceled requests gracefully
		if (axios.isCancel(error) || error.code === 'ERR_CANCELED') {
			// console.log('Request canceled:', error.message);
			return Promise.reject(error);
		}

		const originalRequest = error.config;
		if (
			error.code === "ECONNABORTED" &&
			error.message.indexOf("timeout") !== -1 &&
			!originalRequest._retry
		) {
			originalRequest._retry = true;
			return axios.request(originalRequest);
		}

		let status = 0;
		if (error.response) {
			status = error.response.status;
		} else {
			// Network failure (backend down, CORS, DNS) — surface a friendly localized
			// message instead of leaving the user with a silent failure.
			if (error.code !== 'ERR_CANCELED') {
				console.error('Network error:', error);
				ElMessage.closeAll();
				ElMessage({ type: "error", message: authMsg("networkError") });
			}
			return Promise.reject(error);
		}

		if (status === 401) {
			clearToken();
			router.push({ path: "/login" });
		}
		
		// Only show error message for non-canceled requests
		if (error.code !== 'ERR_CANCELED') {
			ElMessage.closeAll();
			try {
				if (error.response) {
					ElMessage({
						type: "error",
						message: status === 401
							? authMsg("sessionExpired")
							: status === 403
								? authMsg("permissionDenied")
								: error?.response?.data?.message,
					});
				}
			} catch {
				// Empty catch block
			}
		}
		
		return Promise.reject(error);
	},
);

export default axios;
export { addPending, removePending, getPendingKey };
